import { useMemo, useRef, useState } from 'react'
import { Icon } from './Icon'
import { isBlocking, type NavItem, type Problem, type StagedChange, type State } from '../api'
import {
  canMoveDown,
  canMoveUp,
  itemAt,
  wouldCycle,
  type DropTarget,
  type NavContainerPath,
  type NavPath,
} from '../nav-tree'

/**
 * The page rail.
 *
 * Built from nav.yaml, not from filenames, so it cannot disagree with the
 * published sidebar. Three things it does that the first pass did not:
 *
 *  - filters as you type, because scrolling stops working at a few dozen pages
 *  - collapses sections, and remembers which
 *  - surfaces pages MISSING from the nav in their own group. They are the
 *    orphans that block a commit, so burying them would hide the reason a
 *    commit is refused.
 *
 * A fourth thing lives here now: dragging a row to reorder it, move it into a
 * group, or move it to another section — nav.yaml's only editing surface
 * besides the raw YAML tab. Every row that names a place in the tree carries
 * its own `NavPath`, computed while walking `state.nav` the same way the tree
 * itself is walked, so the address a drop reports is never stale by
 * construction.
 *
 * Dragging is POINTER-based — `mousedown`/`mousemove`/`mouseup`, not the
 * native HTML5 drag events — on purpose. It is not a preference: a browser
 * only fires `dragstart`/`dragover` for a genuine OS-level drag gesture, which
 * synthetic `mousemove` calls (the kind a test, or an assistive input device,
 * sends) do not produce. A native `draggable` implementation is therefore not
 * exercisable by the tests this feature is verified with, or by anything else
 * that drives the pointer programmatically, so this rail tracks the mouse
 * itself and never sets `draggable`.
 */
const idToPath = (id: string): string => `pages/${id}.md`

/**
 * A readable name from a slug. The LAST resort, and a poor one: it cannot
 * recover capitalisation the slug never had, so `what-ada-does` becomes "What
 * ada does" and lowercases a proper noun.
 *
 * The order is the page's own title, then a `label` in the navigation file, then
 * this. `/api/state` returns titles now — the tree is read on every request
 * anyway, so throwing them away bought nothing and cost every label in this
 * rail. A title projection in Postgres was planned for it and is not needed.
 */
const leaf = (id: string): string => {
  const last = id.split('/').pop() ?? id
  return last.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/**
 * What a staged row's coloured dot means, in words.
 *
 * `staged write` was the tooltip, which is the API's verb rather than a
 * sentence, and it reached nothing that was not a mouse.
 */
const STAGED_MEANS: Record<StagedChange['op'], string> = {
  write: 'edited, staged for review',
  move: 'moved, staged for review',
  delete: 'deleted, staged for review',
}

const samePath = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i])

const parsePath = (raw: string): NavPath => raw.split('.').map(Number)

/** How far the pointer must travel before a mousedown becomes a drag, in px. */
const DRAG_THRESHOLD = 4

/**
 * What a row can do to itself, beyond being opened.
 *
 * One object rather than six props: these arrive together, they are all
 * absent for the same reason (a viewer, or the read-only live site), and a
 * component that takes six optional callbacks invites five of them to be
 * forgotten at one call site.
 */
export interface RowActions {
  /** A new page, listed under this row. */
  newChild: (under: NavPath) => void
  /** A new section — the headings the sidebar groups pages under. */
  newSection: () => void
  /** Change a section's heading. */
  renameSection: (index: number, label: string) => void
  /** Remove a section. Refused by the caller while it still holds pages. */
  removeSection: (index: number) => void
  /** Change what the navigation lists this row as. */
  rename: (at: NavPath, label: string) => void
  /** A copy of this page, beside it. */
  duplicate: (path: string) => void
  /** The page's own address, on the published site. */
  copyLink: (path: string) => void
  openOnSite: (path: string) => void
  /** Move the page to the trash, from the row rather than the inspector. */
  trash: (path: string) => void
}

interface Props {
  state: State
  selected: string | null
  onSelect: (path: string) => void
  onNewPage: () => void
  /** Apply a reorder. Never called when `readOnly` is set. */
  onReorder: (from: NavPath, to: DropTarget) => void
  /** A viewer, or a branch nothing can be staged on: dragging is disabled. */
  readOnly: boolean
  /** Absent for anybody who may not write — see `RowActions`. */
  actions?: RowActions
}

export function Sidebar({
  state,
  selected,
  onSelect,
  onNewPage,
  onReorder,
  readOnly,
  actions,
}: Props) {
  const [filter, setFilter] = useState('')
  /**
   * Which groups are shut, remembered per site.
   *
   * A tree that reopens everything on every load is a tree somebody closes
   * again every morning — and the reason to close a section is that it is
   * not what you are working on this week, which outlives a page load. Per
   * SITE, because two docs sites in one workspace have different trees and
   * sharing the answer would collapse a section in one because a section
   * with the same name was shut in the other.
   */
  const collapseKeyFor = `staleless:collapsed:${state.repo}`
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const kept = localStorage.getItem(collapseKeyFor)
      return new Set(kept ? (JSON.parse(kept) as string[]) : [])
    } catch {
      return new Set()
    }
  })
  const [dragPath, setDragPath] = useState<NavPath | null>(null)
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)
  /**
   * What the drop would DO, in words, and where the pointer is so it can be
   * said there.
   *
   * A thin blue line says "something will land here" and nothing else. It
   * cannot say whether a page is about to become a child of the row above or
   * a sibling after it — which is the one thing that is easy to get wrong and
   * costs a published URL when it is. So the drag carries a label.
   */
  const [ghost, setGhost] = useState<{ x: number; y: number; says: string } | null>(null)
  /**
   * Set the instant a mousedown-drag crosses the threshold, and read by the
   * row's own `onClick` right after `mouseup` — a click fires there whether or
   * not the pointer moved in between, and a drag that just repositioned a page
   * must not also open it.
   */
  const didDragRef = useRef(false)
  /** Which row has its "⋯" menu open, by nav path. Only ever one. */
  const [menuAt, setMenuAt] = useState<string | null>(null)
  /** Which row is being renamed in place, and what has been typed so far. */
  const [renaming, setRenaming] = useState<{ path: string; value: string } | null>(null)

  const stagedBy = useMemo(() => new Map(state.staged.map((s) => [s.path, s])), [state.staged])
  const problemsBy = useMemo(() => {
    const map = new Map<string, Problem[]>()
    for (const p of state.lint.problems) map.set(p.file, [...(map.get(p.file) ?? []), p])
    return map
  }, [state.lint.problems])

  const listed = useMemo(() => {
    const set = new Set<string>()
    const walk = (items: NavItem[]): void => {
      for (const item of items) {
        if (item.page) set.add(item.page)
        if (item.items) walk(item.items)
      }
    }
    for (const s of state.nav.sections) walk(s.items)
    return set
  }, [state.nav])

  const q = filter.trim().toLowerCase()
  const matches = (path: string, label: string) =>
    !q || path.toLowerCase().includes(q) || label.toLowerCase().includes(q)

  /** A page's own title, by id. Built once: the rail renders every entry. */
  const titles = new Map(state.pages.map((p) => [p.id, p.title]))
  const titleOf = (id: string): string | null => titles.get(id) ?? null

  const orphans = state.pages.filter((p) => !listed.has(p.id) && p.id !== 'index')
  const index = state.pages.find((p) => p.id === 'index')

  /**
   * Does the filter hide every page?
   *
   * A word that matched nothing used to empty the tree in silence: no rows, no
   * message, and no way back except noticing that the box still holds the
   * word. Asked of PAGES only, because an external link is never filtered —
   * it stays on screen, and the message says "page" for that reason.
   */
  const everyPageRow = (): { path: string; label: string }[] => {
    const rows: { path: string; label: string }[] = []
    if (index) rows.push({ path: index.path, label: 'Home' })
    const walk = (items: NavItem[]): void => {
      for (const item of items) {
        if (item.page) {
          rows.push({
            path: idToPath(item.page),
            label: item.label ?? titleOf(item.page) ?? leaf(item.page),
          })
        }
        if (item.items) walk(item.items)
      }
    }
    for (const section of state.nav.sections) walk(section.items)
    for (const p of orphans) rows.push({ path: p.path, label: p.title ?? leaf(p.id) })
    return rows
  }
  const allRows = everyPageRow()
  const filterHitsNothing = q !== '' && !allRows.some((r) => matches(r.path, r.label))
  /**
   * No pages at all — every one deleted, on a draft that started from nothing.
   * Rare, and the one state where this rail showed a bare white column with two
   * buttons at its foot and no clue what the column was.
   */
  const treeIsEmpty = q === '' && allRows.length === 0

  /**
   * Dragging is off while a filter narrows what is on screen. `q` hides rows
   * without touching the tree, so the rows a drag would see are not the
   * container's real, contiguous order — a drop computed from them would
   * insert at the wrong index in the file that is actually written.
   */
  const dragEnabled = !readOnly && !q

  /**
   * The drop this element would produce, read off the `data-*` attributes set
   * by whichever row or header is under the pointer right now. Kept as plain
   * attributes, rather than a lookup by path, so the one function that already
   * finds "what's under the cursor" — `elementFromPoint` — is also the one
   * that identifies it; there is no second, parallel index of the tree to keep
   * in step with the one React just rendered.
   */
  const targetFor = (el: HTMLElement, clientY: number): DropTarget | null => {
    const kind = el.dataset['dropKind']
    const ownPath = el.dataset['ownPath'] ? parsePath(el.dataset['ownPath']) : null
    if (!kind || !ownPath) return null
    const rect = el.getBoundingClientRect()
    const childCount = Number(el.dataset['childCount'] ?? 0)

    if (kind === 'section') return { containerPath: ownPath, beforeIndex: childCount }

    const index = ownPath[ownPath.length - 1]!
    const containerPath = ownPath.slice(0, -1)
    if (kind === 'group') {
      const y = (clientY - rect.top) / rect.height
      if (y < 0.25) return { containerPath, beforeIndex: index }
      if (y > 0.75) return { containerPath, beforeIndex: index + 1 }
      return { containerPath: ownPath, beforeIndex: childCount }
    }
    if (kind === 'page') {
      // Three bands, like a group: the middle NESTS the drag under this page,
      // GitBook-style. The edges stay siblings, so precise ordering is never
      // sacrificed to nesting.
      const y = (clientY - rect.top) / rect.height
      if (y < 0.3) return { containerPath, beforeIndex: index }
      if (y > 0.7) return { containerPath, beforeIndex: index + 1 }
      return { containerPath: ownPath, beforeIndex: childCount }
    }
    // A leaf (an external link): only ever a sibling, split at the midpoint.
    const before = clientY < rect.top + rect.height / 2
    return { containerPath, beforeIndex: before ? index : index + 1 }
  }

  /**
   * What a drop at `target` would do, as a sentence about a named row.
   *
   * Read off the tree rather than off the pointer: "into Workflow" and
   * "after Publishing" are different outcomes at two pixels' difference, and
   * the person dragging is looking at the row, not at the indicator.
   */
  const describeDrop = (target: DropTarget): string => {
    const nameOf = (item: NavItem | null): string =>
      item
        ? (item.label ?? (item.page ? (titleOf(item.page) ?? leaf(item.page)) : 'the section'))
        : 'the section'

    const container = target.containerPath
    // A container that is an ITEM (a group, or a page with children) means the
    // drop nests; a section container means it sits among that section's rows.
    const holder = container.length >= 2 ? itemAt(state.nav, container) : null
    if (holder) return `Move into ${nameOf(holder)}`

    const section = state.nav.sections[container[0] ?? 0]
    const siblings = section?.items ?? []
    const before = siblings[target.beforeIndex - 1]
    const after = siblings[target.beforeIndex]
    if (before) return `Move after ${nameOf(before)}`
    if (after) return `Move before ${nameOf(after)}`
    return `Move into ${section?.label ?? 'the section'}`
  }

  /**
   * Arms a drag on mousedown, and runs it entirely through closures over
   * plain local variables — not component state — until it ends. State
   * updates still drive what is drawn (the dimmed source row, the insertion
   * line), but the DECISION of where a drop would land never round-trips
   * through a render: `elementFromPoint` is asked fresh on every `mousemove`,
   * so a drop always resolves against whatever is under the pointer at that
   * instant, not a value captured when the drag began.
   */
  const beginDrag = (e: React.MouseEvent, path: NavPath) => {
    if (!dragEnabled || e.button !== 0) return
    if ((e.target as HTMLElement).closest('.nav-row__moves')) return
    const startX = e.clientX
    const startY = e.clientY
    let active = false
    let target: DropTarget | null = null

    /** Everything a drag installs, taken down exactly once however it ends. */
    const stop = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('keydown', onKey, true)
      document.body.classList.remove('is-dragging-nav')
      setDragPath(null)
      setDropTarget(null)
      setGhost(null)
    }

    const onMove = (ev: MouseEvent) => {
      if (!active) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < DRAG_THRESHOLD) return
        active = true
        didDragRef.current = true
        setDragPath(path)
        document.body.classList.add('is-dragging-nav')
      }
      /*
       * The tree is taller than the column, and a drag that cannot reach the
       * rows below the fold can only move a page a little way. Nearing either
       * edge scrolls the tree under the pointer, which is what every outliner
       * does and what nothing here did.
       */
      const scroller = document.querySelector<HTMLElement>('.rail__scroll')
      if (scroller) {
        const box = scroller.getBoundingClientRect()
        const EDGE = 36
        if (ev.clientY < box.top + EDGE) scroller.scrollTop -= 12
        else if (ev.clientY > box.bottom - EDGE) scroller.scrollTop += 12
      }
      const el = document.elementFromPoint(ev.clientX, ev.clientY)
      const dropEl = (el as HTMLElement | null)?.closest<HTMLElement>('[data-drop-kind]') ?? null
      const found = dropEl ? targetFor(dropEl, ev.clientY) : null
      target = found && !wouldCycle(path, found.containerPath) ? found : null
      setDropTarget(target)
      setGhost({
        x: ev.clientX,
        y: ev.clientY,
        says: target ? describeDrop(target) : 'Nowhere to drop here',
      })
    }
    const onUp = () => {
      const landing = target
      const moved = active
      stop()
      if (moved && landing) onReorder(path, landing)
    }
    /**
     * Escape gives the drag back.
     *
     * Until now the only way out of a drag begun by accident was to find a
     * spot that refuses drops — and the safest-looking one, the row you
     * started from, is a real move. Capture, so nothing else answers first.
     */
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return
      ev.preventDefault()
      ev.stopPropagation()
      target = null
      stop()
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('keydown', onKey, true)
  }

  /** A click right after a drag is the mouseup that ended it, not a request to open the row. */
  const selectUnlessDragged = (path: string) => {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }
    onSelect(path)
  }

  /** The thin line a drop would insert at, rendered between two rows. */
  const dropLine = (
    containerPath: NavContainerPath,
    atIndex: number,
    key: string,
    depth: number,
  ) =>
    dragPath &&
    dropTarget &&
    samePath(dropTarget.containerPath, containerPath) &&
    dropTarget.beforeIndex === atIndex ? (
      <div
        className="nav-drop-line"
        key={`dropline-${key}`}
        style={{ marginLeft: `calc(var(--sp-3) + ${depth * 12}px)` }}
      />
    ) : null

  /**
   * The keyboard's half of dragging.
   *
   * A tree that can only be rearranged with a pointer is a tree half the
   * people who use it cannot rearrange. Alt with an arrow is the convention
   * every outliner shares: up and down reorder among siblings, right nests
   * under the row above, left promotes out of the parent.
   */
  const onRowKey = (e: React.KeyboardEvent, navPath: NavPath) => {
    if (!e.altKey || readOnly) return
    const container = navPath.slice(0, -1)
    const index = navPath[navPath.length - 1]!
    if (e.key === 'ArrowUp' && canMoveUp(state.nav, navPath)) {
      e.preventDefault()
      onReorder(navPath, { containerPath: container, beforeIndex: index - 1 })
    } else if (e.key === 'ArrowDown' && canMoveDown(state.nav, navPath)) {
      e.preventDefault()
      onReorder(navPath, { containerPath: container, beforeIndex: index + 2 })
    } else if (e.key === 'ArrowRight' && index > 0) {
      // Under the sibling above, at the end of whatever it already holds.
      e.preventDefault()
      const above: NavPath = [...container, index - 1]
      const item = itemAt(state.nav, above)
      const count = item?.items?.length ?? 0
      onReorder(navPath, { containerPath: above, beforeIndex: count })
    } else if (e.key === 'ArrowLeft' && navPath.length > 2) {
      // Out of the parent, and immediately after it.
      e.preventDefault()
      const parent = container
      const parentIndex = parent[parent.length - 1]!
      onReorder(navPath, {
        containerPath: parent.slice(0, -1),
        beforeIndex: parentIndex + 1,
      })
    }
  }

  /** The "⋯" menu for one page row: everything that is not opening it. */
  const rowMenu = (navPath: NavPath | null, path: string, label: string) => {
    if (!actions) return null
    const key = navPath ? navPath.join('.') : `orphan:${path}`
    const isPage = path.startsWith('pages/')
    return (
      <span className="nav-row__menuwrap">
        {navPath && (
          <button
            type="button"
            className="nav-row__move"
            data-tip={`New page under "${label}"`}
            onClick={(e) => {
              e.stopPropagation()
              actions.newChild(navPath)
            }}
          >
            <Icon name="plus" size={11} label={`New page under ${label}`} />
          </button>
        )}
        <button
          type="button"
          className="nav-row__move"
          aria-haspopup="menu"
          aria-expanded={menuAt === key}
          data-tip={`More for "${label}"`}
          onClick={(e) => {
            e.stopPropagation()
            setMenuAt(menuAt === key ? null : key)
          }}
        >
          <span className="nav-row__dots" aria-hidden>
            ⋯
          </span>
          <span className="sr-only">More for {label}</span>
        </button>
        {menuAt === key && (
          <>
            <div className="popover-scrim" onMouseDown={() => setMenuAt(null)} />
            <div className="rowmenu" role="menu" aria-label={`Actions for ${label}`}>
              {navPath && (
                <button
                  role="menuitem"
                  className="switcher__item"
                  onClick={() => {
                    setMenuAt(null)
                    setRenaming({ path: navPath.join('.'), value: label })
                  }}
                >
                  <Icon name="rename" size={13} />
                  Rename
                </button>
              )}
              {isPage && (
                <button
                  role="menuitem"
                  className="switcher__item"
                  onClick={() => {
                    setMenuAt(null)
                    actions.duplicate(path)
                  }}
                >
                  <Icon name="pages" size={13} />
                  Duplicate
                </button>
              )}
              {isPage && (
                <button
                  role="menuitem"
                  className="switcher__item"
                  onClick={() => {
                    setMenuAt(null)
                    actions.copyLink(path)
                  }}
                >
                  <Icon name="link" size={13} />
                  Copy link
                </button>
              )}
              {isPage && (
                <button
                  role="menuitem"
                  className="switcher__item"
                  onClick={() => {
                    setMenuAt(null)
                    actions.openOnSite(path)
                  }}
                >
                  <Icon name="eye" size={13} />
                  Open on site
                </button>
              )}
              {isPage && (
                <button
                  role="menuitem"
                  className="switcher__item"
                  onClick={() => {
                    setMenuAt(null)
                    actions.trash(path)
                  }}
                >
                  <Icon name="trash" size={13} />
                  Move to trash
                </button>
              )}
            </div>
          </>
        )}
      </span>
    )
  }

  const moveButtons = (path: NavPath, label: string) => {
    if (readOnly) return null
    return (
      <span className="nav-row__moves">
        <button
          type="button"
          className="nav-row__move"
          disabled={!canMoveUp(state.nav, path)}
          onClick={(e) => {
            e.stopPropagation()
            const idx = path[path.length - 1]!
            onReorder(path, { containerPath: path.slice(0, -1), beforeIndex: idx - 1 })
          }}
          data-tip={`Move "${label}" up`}
        >
          <Icon name="chevron-up" size={11} label={`Move ${label} up`} />
        </button>
        <button
          type="button"
          className="nav-row__move"
          disabled={!canMoveDown(state.nav, path)}
          onClick={(e) => {
            e.stopPropagation()
            const idx = path[path.length - 1]!
            onReorder(path, { containerPath: path.slice(0, -1), beforeIndex: idx + 2 })
          }}
          data-tip={`Move "${label}" down`}
        >
          <Icon name="chevron-down" size={11} label={`Move ${label} down`} />
        </button>
      </span>
    )
  }

  /** A page row. `navPath` is null for the home page and for orphans — neither is draggable. */
  const row = (
    path: string,
    label: string,
    depth: number,
    navPath: NavPath | null,
    missing = false,
    /** How many children this page carries — a PARENT page, GitBook-style. */
    childCount = 0,
    collapseKey: string | null = null,
  ) => {
    if (!matches(path, label)) return null
    const staged = stagedBy.get(path)
    const problems = problemsBy.get(path) ?? []
    const blockers = problems.filter(isBlocking).length
    const blocking = blockers > 0
    const draggableHere = dragEnabled && navPath !== null
    const nestHere =
      navPath &&
      dropTarget &&
      dragPath &&
      samePath(dropTarget.containerPath, navPath) &&
      dropTarget.beforeIndex === childCount
    return (
      <div
        key={path}
        className={`nav-row${navPath && dragPath && samePath(dragPath, navPath) ? ' is-dragging' : ''}`}
        onMouseDown={draggableHere ? (e) => beginDrag(e, navPath!) : undefined}
        data-drop-kind={draggableHere ? 'page' : undefined}
        data-own-path={draggableHere ? navPath!.join('.') : undefined}
        data-child-count={draggableHere ? childCount : undefined}
      >
        <button
          className={`nav-page${selected === path ? ' is-selected' : ''}${missing ? ' is-missing' : ''}${nestHere ? ' is-drop-target' : ''}`}
          style={{ paddingLeft: `calc(var(--sp-3) + ${depth * 12}px)` }}
          onClick={() => selectUnlessDragged(path)}
          onKeyDown={navPath ? (e) => onRowKey(e, navPath) : undefined}
          /* The address, always. It used to be replaced by a dump of the lint
             messages the moment a page had any — so the one tooltip in the
             rail did two unrelated jobs and did the second one only sometimes,
             and the address of a truncated row became unreadable exactly on
             the rows somebody was investigating. The messages have a list of
             their own in the inspector, which is where they are acted on. */
          data-tip={path}
        >
          {collapseKey !== null ? (
            <span
              role="button"
              tabIndex={0}
              className="nav-page__chevron"
              aria-label={collapsed.has(collapseKey) ? 'Expand' : 'Collapse'}
              onClick={(ev) => {
                ev.stopPropagation()
                toggle(collapseKey)
              }}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault()
                  ev.stopPropagation()
                  toggle(collapseKey)
                }
              }}
            >
              <Icon
                name={collapsed.has(collapseKey) ? 'chevron-right' : 'chevron-down'}
                size={11}
              />
            </span>
          ) : (
            <Icon name="page" size={13} className="nav-page__icon" />
          )}
          {renaming && navPath && renaming.path === navPath.join('.') ? (
            /* Renaming happens where the name IS. A dialog for one word puts
               the answer somewhere other than the question. */
            <input
              className="nav-page__rename"
              value={renaming.value}
              autoFocus
              aria-label={`Rename ${label}`}
              onClick={(ev) => ev.stopPropagation()}
              onMouseDown={(ev) => ev.stopPropagation()}
              onChange={(ev) => setRenaming({ path: renaming.path, value: ev.target.value })}
              onBlur={() => {
                actions?.rename(navPath, renaming.value)
                setRenaming(null)
              }}
              onKeyDown={(ev) => {
                ev.stopPropagation()
                if (ev.key === 'Enter') {
                  ev.preventDefault()
                  actions?.rename(navPath, renaming.value)
                  setRenaming(null)
                } else if (ev.key === 'Escape') {
                  ev.preventDefault()
                  setRenaming(null)
                }
              }}
            />
          ) : (
            <span className="nav-page__label truncate">{label}</span>
          )}
          {/*
            Two marks, six pixels across, and until now the only thing that
            said what either one meant was a `title`.

            A tooltip is a mouse affordance: no browser shows one on keyboard
            focus, a touch has nowhere to hover, and a `<span>` takes no focus
            in any case. So the row announced itself as "Getting started" and
            said nothing at all about the red dot beside the name — the mark
            that means this page is why the commit is refused.

            The words are in the row's own accessible name now. It costs no
            space because it takes none: `.sr-only` is out of flow, the dot is
            drawn exactly as it was, and the button reads "Getting started, 2
            problems, blocking the commit." What those problems SAY is in the
            inspector's Checks list, which is one press away and is where the
            messages already live.
          */}
          {staged && (
            <span className={`dot dot--${staged.op}`}>
              <span className="sr-only">{STAGED_MEANS[staged.op] ?? `staged ${staged.op}`}</span>
            </span>
          )}
          {blocking && (
            <span className="dot dot--problem">
              <span className="sr-only">
                {blockers === 1
                  ? '1 problem, blocking the commit'
                  : `${blockers} problems, blocking the commit`}
              </span>
            </span>
          )}
        </button>
        {navPath && moveButtons(navPath, label)}
        {rowMenu(navPath, path, label)}
      </div>
    )
  }

  const renderItems = (
    items: NavItem[],
    depth: number,
    containerPath: NavContainerPath,
    startIndex = 0,
  ): React.ReactNode[] =>
    items.flatMap((item, i) => {
      const navIndex = i + startIndex
      const ownPath: NavPath = [...containerPath, navIndex]
      const before = dropLine(
        containerPath,
        navIndex,
        `${containerPath.join('-')}-${navIndex}`,
        depth,
      )

      if (item.page) {
        const has = state.pages.some((p) => p.id === item.page)
        const children = item.items ?? []
        const collapseKey = children.length > 0 ? `page:${item.page}` : null
        const shut = collapseKey !== null && collapsed.has(collapseKey)
        // The navigation file may name the page; prefer that over the slug.
        return [
          before,
          row(
            idToPath(item.page),
            // A `label` in the navigation is a deliberate override of the
            // page's own title, so it wins first; the title is the fallback,
            // and de-slugifying the filename is the last resort of the two.
            item.label ?? titleOf(item.page) ?? leaf(item.page),
            depth,
            ownPath,
            !has,
            children.length,
            collapseKey,
          ),
          // The cascade: children indent beneath their parent and collapse
          // behind its chevron — the same tree the published sidebar shows.
          ...(shut ? [] : renderItems(children, depth + 1, ownPath)),
          ...(shut
            ? []
            : [dropLine(ownPath, children.length, `${ownPath.join('-')}-end`, depth + 1)]),
        ]
      }
      const key = `${item.label}-${depth}-${i}`

      /**
       * A separator — a rule between items, with its small caption when it has
       * one. Draggable like everything else, because where it sits IS what it
       * says; never a button, because there is nothing behind it to open; and
       * `data-drop-kind="leaf"`, so a drop beside it is a sibling and nothing
       * is ever nested inside a rule.
       *
       * It ignores the filter box on purpose: a rule matches no search text,
       * and a rail filtered to two pages should not draw the rules that used
       * to sit between them and the rest.
       */
      if (item.separator) {
        if (q) return []
        const dragging = dragPath && samePath(dragPath, ownPath)
        return [
          before,
          <div
            key={`separator-${key}-${i}`}
            className={`nav-row nav-row--separator${dragging ? ' is-dragging' : ''}`}
            onMouseDown={dragEnabled ? (e) => beginDrag(e, ownPath) : undefined}
            data-drop-kind={dragEnabled ? 'leaf' : undefined}
            data-own-path={dragEnabled ? ownPath.join('.') : undefined}
          >
            <div
              className="nav-separator"
              style={{ marginLeft: `calc(var(--sp-3) + ${depth * 12}px)` }}
              role="separator"
              aria-label={item.label ? `Separator: ${item.label}` : 'Separator'}
            >
              {item.label && <span className="nav-separator__label">{item.label}</span>}
              <span className="nav-separator__rule" />
            </div>
            {moveButtons(ownPath, item.label ?? 'Separator')}
          </div>,
        ]
      }

      /**
       * A link out of the site. Shown, and not editable here: there is no page
       * to open, and leaving it out would make the rail disagree with the
       * published sidebar — which is the one thing this rail is for.
       */
      if ('href' in item && item.href) {
        const dragging = dragPath && samePath(dragPath, ownPath)
        return [
          before,
          <div
            key={key}
            className={`nav-row${dragging ? ' is-dragging' : ''}`}
            onMouseDown={dragEnabled ? (e) => beginDrag(e, ownPath) : undefined}
            data-drop-kind={dragEnabled ? 'leaf' : undefined}
            data-own-path={dragEnabled ? ownPath.join('.') : undefined}
          >
            <a
              className="nav-page nav-page--external"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ paddingLeft: `calc(var(--sp-3) + ${depth * 12}px)` }}
              data-tip={item.href}
              onClick={(e) => {
                // The mousedown that started a drag also focuses the anchor;
                // a drag that ends over its own row must not then follow the
                // link, which a real drop never intends.
                if (didDragRef.current) {
                  e.preventDefault()
                  didDragRef.current = false
                }
              }}
            >
              <Icon name="link" size={13} className="nav-page__icon" />
              <span className="nav-page__label truncate">{item.label}</span>
            </a>
            {moveButtons(ownPath, item.label ?? 'Link')}
          </div>,
        ]
      }

      const children = item.items ?? []
      const dragging = dragPath && samePath(dragPath, ownPath)
      const appendHere =
        dropTarget && dragPath && samePath(dropTarget.containerPath, ownPath) ? dropTarget : null
      return [
        before,
        <div
          key={key}
          className={`nav-row nav-row--group${dragging ? ' is-dragging' : ''}`}
          onMouseDown={dragEnabled ? (e) => beginDrag(e, ownPath) : undefined}
          data-drop-kind={dragEnabled ? 'group' : undefined}
          data-own-path={dragEnabled ? ownPath.join('.') : undefined}
          data-child-count={dragEnabled ? children.length : undefined}
        >
          <div
            className={`nav-group${appendHere && appendHere.beforeIndex === children.length ? ' is-drop-target' : ''}`}
            style={{ paddingLeft: `calc(var(--sp-3) + ${depth * 12}px)` }}
          >
            {item.label}
          </div>
          {moveButtons(ownPath, item.label ?? 'Section')}
        </div>,
        ...renderItems(children, depth + 1, ownPath),
        dropLine(ownPath, children.length, `${key}-end`, depth + 1),
      ]
    })

  const toggle = (label: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      try {
        localStorage.setItem(collapseKeyFor, JSON.stringify([...next]))
      } catch {
        // Forgetting between visits is the only cost.
      }
      return next
    })

  return (
    <nav className="rail" aria-label="Pages">
      {/* What the drop would do, beside the pointer. `aria-live` because a
          drag is the one gesture where the outcome is not written anywhere
          on the screen it is about to change. */}
      {ghost && (
        <div
          className="nav-ghost"
          style={{ left: `${ghost.x + 14}px`, top: `${ghost.y + 14}px` }}
          role="status"
          aria-live="polite"
        >
          {ghost.says}
        </div>
      )}
      {/* Which site these pages belong to is the switcher's job now — the
          row above this column names it once for the whole shell. */}
      <div className="rail__search">
        <Icon name="search" size={14} />
        <input
          className="rail__input"
          placeholder="Filter pages"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter pages"
        />
        {filter && (
          <button className="btn btn--ghost btn--icon btn--sm" onClick={() => setFilter('')}>
            <Icon name="close" size={12} label="Clear filter" />
          </button>
        )}
      </div>

      <div className="rail__scroll">
        {index && <div className="rail__section">{row(index.path, 'Home', 0, null)}</div>}

        {state.nav.sections.map((section, sectionIndex) => {
          const isCollapsed = collapsed.has(section.label) && !q
          const containerPath: NavContainerPath = [sectionIndex]
          // A section's first Overview page is its landing page. It belongs
          // to the section heading, so showing it again as a child creates the
          // confusing "Ada / Ada" duplicate in the rail.
          const landing = section.items[0]
          const landingPage = landing?.page
          const hasLanding = Boolean(landingPage && landing?.label === 'Overview')
          const children = renderItems(
            hasLanding ? section.items.slice(1) : section.items,
            0,
            containerPath,
            hasLanding ? 1 : 0,
          ).filter(Boolean)
          if (q && children.length === 0) return null
          const sectionDrop =
            dropTarget && dragPath && samePath(dropTarget.containerPath, containerPath)
              ? dropTarget
              : null
          const menuKey = `section:${sectionIndex}`
          const renamingThis = renaming?.path === menuKey
          return (
            <div className="rail__section" key={section.label}>
              <div className="rail__sectionrow">
                <button
                  className={`rail__section-head${landingPage && selected === idToPath(landingPage) ? ' is-selected' : ''}${sectionDrop && sectionDrop.beforeIndex === section.items.length ? ' is-drop-target' : ''}`}
                  onClick={() => {
                    if (landingPage && hasLanding) selectUnlessDragged(idToPath(landingPage))
                    else toggle(section.label)
                  }}
                  aria-expanded={!isCollapsed}
                  aria-label={landingPage && hasLanding ? `Open ${section.label}` : undefined}
                  data-drop-kind={dragEnabled ? 'section' : undefined}
                  data-own-path={dragEnabled ? containerPath.join('.') : undefined}
                  data-child-count={dragEnabled ? section.items.length : undefined}
                >
                  <Icon name={isCollapsed ? 'chevron-right' : 'chevron-down'} size={12} />
                  {renamingThis ? (
                    <input
                      className="nav-page__rename"
                      value={renaming.value}
                      autoFocus
                      aria-label={`Rename ${section.label}`}
                      onClick={(ev) => ev.stopPropagation()}
                      onChange={(ev) => setRenaming({ path: menuKey, value: ev.target.value })}
                      onBlur={() => {
                        actions?.renameSection(sectionIndex, renaming.value)
                        setRenaming(null)
                      }}
                      onKeyDown={(ev) => {
                        ev.stopPropagation()
                        if (ev.key === 'Enter') {
                          ev.preventDefault()
                          actions?.renameSection(sectionIndex, renaming.value)
                          setRenaming(null)
                        } else if (ev.key === 'Escape') {
                          ev.preventDefault()
                          setRenaming(null)
                        }
                      }}
                    />
                  ) : (
                    <span className="label">{section.label}</span>
                  )}
                </button>
                {actions && (
                  <span className="nav-row__menuwrap">
                    <button
                      type="button"
                      className="nav-row__move"
                      data-tip={`New page in "${section.label}"`}
                      onClick={(e) => {
                        e.stopPropagation()
                        actions.newChild(containerPath as NavPath)
                      }}
                    >
                      <Icon name="plus" size={11} label={`New page in ${section.label}`} />
                    </button>
                    <button
                      type="button"
                      className="nav-row__move"
                      aria-haspopup="menu"
                      aria-expanded={menuAt === menuKey}
                      data-tip={`More for "${section.label}"`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuAt(menuAt === menuKey ? null : menuKey)
                      }}
                    >
                      <span className="nav-row__dots" aria-hidden>
                        ⋯
                      </span>
                      <span className="sr-only">More for {section.label}</span>
                    </button>
                    {menuAt === menuKey && (
                      <>
                        <div className="popover-scrim" onMouseDown={() => setMenuAt(null)} />
                        <div
                          className="rowmenu"
                          role="menu"
                          aria-label={`Actions for ${section.label}`}
                        >
                          <button
                            role="menuitem"
                            className="switcher__item"
                            onClick={() => {
                              setMenuAt(null)
                              setRenaming({ path: menuKey, value: section.label })
                            }}
                          >
                            <Icon name="rename" size={13} />
                            Rename section
                          </button>
                          <button
                            role="menuitem"
                            className="switcher__item"
                            onClick={() => {
                              setMenuAt(null)
                              actions.newSection()
                            }}
                          >
                            <Icon name="plus" size={13} />
                            New section
                          </button>
                          <button
                            role="menuitem"
                            className="switcher__item"
                            onClick={() => {
                              setMenuAt(null)
                              actions.removeSection(sectionIndex)
                            }}
                          >
                            <Icon name="trash" size={13} />
                            Delete section
                          </button>
                        </div>
                      </>
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && children}
            </div>
          )
        })}

        {orphans.length > 0 && (
          <div className="rail__section">
            <div className="rail__section-head rail__section-head--warn">
              <Icon name="alert" size={12} />
              <span className="label">Not in navigation</span>
              <span className="badge badge--warn">{orphans.length}</span>
            </div>
            {orphans.map((p) => row(p.path, p.title ?? leaf(p.id), 0, null))}
            <p className="rail__note">
              A page the navigation does not list is an orphan, and the commit will be refused until
              it is added.
            </p>
          </div>
        )}

        {filterHitsNothing && (
          <div className="rail__section rail__section--none">
            <p className="nothing">No page matches &ldquo;{filter.trim()}&rdquo;.</p>
            <button className="btn btn--ghost btn--sm" onClick={() => setFilter('')}>
              <Icon name="close" size={12} />
              Clear the filter
            </button>
          </div>
        )}

        {treeIsEmpty && (
          <div className="rail__section rail__section--none">
            <p className="nothing">
              No pages here. This column is the site&rsquo;s navigation: every page readers can
              reach, in the order they see them.
            </p>
            {/* The same rule as the foot's own button: offered only where a
                page can actually be written. */}
            {!readOnly && (
              <button className="btn btn--ghost btn--sm" onClick={onNewPage}>
                <Icon name="plus" size={12} />
                New page
              </button>
            )}
          </div>
        )}
      </div>

      {/* A control that would only refuse is not offered: a viewer never
          writes, and on the live site the way to a new page is a draft. The
          whole-site files and the trash live behind the tab header's own
          menu — they are about the site, not about the next page. */}
      {!readOnly && (
        <div className="rail__foot">
          <button className="btn btn--ghost btn--sm rail__foot-act" onClick={onNewPage}>
            <Icon name="plus" size={13} />
            New page
          </button>
        </div>
      )}
    </nav>
  )
}
